import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { CalendarApiService } from '../../services/calendar-api/CalendarApiService';
import {
  CalendarEntry,
  SaveCalendarEntryData,
  WorkspacePriority,
} from '../../types/Workspace';

interface CalendarDay {
  date: Date;
  dateKey: string;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  entries: CalendarEntry[];
}

interface CalendarEntryForm {
  title: string;
  date: string;
  startsAt: string;
  endsAt: string;
  priority: WorkspacePriority;
  description: string;
}

@Component({
  selector: 'app-calendar-page',
  standalone: false,
  templateUrl: './calendar-page.component.html',
  styleUrl: './calendar-page.component.sass',
})
export class CalendarPageComponent implements OnInit {
  public entries: CalendarEntry[] = [];
  public displayedMonth = this.startOfMonth(new Date());
  public selectedDateKey = this.toDateKey(new Date());
  public modal: 'create' | 'details' | null = null;
  public selectedEntry: CalendarEntry | null = null;
  public formData: CalendarEntryForm = this.createForm();
  public loading = false;
  public saving = false;
  public errorMessage: string | null = null;
  public successMessage: string | null = null;

  public readonly weekdayNames = [
    'Пн',
    'Вт',
    'Ср',
    'Чт',
    'Пт',
    'Сб',
    'Вс',
  ];

  public readonly priorityOptions: {
    value: WorkspacePriority;
    label: string;
  }[] = [
    {
      value: 'low',
      label: 'Низкая',
    },
    {
      value: 'medium',
      label: 'Обычная',
    },
    {
      value: 'high',
      label: 'Важная',
    },
    {
      value: 'urgent',
      label: 'Срочная',
    },
  ];

  public constructor(
    private calendarApi: CalendarApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.loadEntries();
  }

  public get monthLabel(): string {
    return new Intl.DateTimeFormat('ru-RU', {
      month: 'long',
      year: 'numeric',
    }).format(this.displayedMonth);
  }

  public get calendarDays(): CalendarDay[] {
    const firstDay = new Date(this.displayedMonth.getFullYear(), this.displayedMonth.getMonth(), 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const gridStart = new Date(firstDay);
    gridStart.setDate(firstDay.getDate() - startOffset);
    const month = this.displayedMonth.getMonth();
    const todayKey = this.toDateKey(new Date());

    return Array.from({ length: 42 }, (_value, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      const dateKey = this.toDateKey(date);
      return {
        date,
        dateKey,
        dayNumber: date.getDate(),
        isCurrentMonth: date.getMonth() === month,
        isToday: dateKey === todayKey,
        entries: this.entries.filter((entry) => this.toDateKey(new Date(entry.startsAt)) === dateKey),
      };
    });
  }

  public get selectedDate(): Date {
    return this.dateFromKey(this.selectedDateKey);
  }

  public get selectedEntries(): CalendarEntry[] {
    return this.entries
      .filter((entry) => this.toDateKey(new Date(entry.startsAt)) === this.selectedDateKey)
      .sort((left, right) => new Date(left.startsAt).getTime() - new Date(right.startsAt).getTime());
  }

  public get upcomingEntries(): CalendarEntry[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return this.entries
      .filter((entry) => new Date(entry.startsAt) >= today)
      .sort((left, right) => new Date(left.startsAt).getTime() - new Date(right.startsAt).getTime())
      .slice(0, 4);
  }

  public async showPreviousMonth(): Promise<void> {
    this.displayedMonth = new Date(this.displayedMonth.getFullYear(), this.displayedMonth.getMonth() - 1, 1);
    this.selectedDateKey = this.toDateKey(this.displayedMonth);
    await this.loadEntries();
  }

  public async showNextMonth(): Promise<void> {
    this.displayedMonth = new Date(this.displayedMonth.getFullYear(), this.displayedMonth.getMonth() + 1, 1);
    this.selectedDateKey = this.toDateKey(this.displayedMonth);
    await this.loadEntries();
  }

  public async showCurrentMonth(): Promise<void> {
    this.displayedMonth = this.startOfMonth(new Date());
    this.selectedDateKey = this.toDateKey(new Date());
    await this.loadEntries();
  }

  public selectDay(day: CalendarDay): void {
    this.selectedDateKey = day.dateKey;
  }

  public openCreate(date = this.selectedDate): void {
    this.selectedEntry = null;
    this.formData = this.createForm(date);
    this.modal = 'create';
  }

  public openDetails(event: MouseEvent, entry: CalendarEntry): void {
    event.stopPropagation();
    this.selectedEntry = entry;
    this.modal = 'details';
  }

  public closeModal(): void {
    if (this.saving) {
      return;
    }

    this.modal = null;
    this.selectedEntry = null;
  }

  public async saveEntry(): Promise<void> {
    if (!this.formData.title.trim()) {
      this.errorMessage = 'Укажите название события.';
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    try {
      const response = await this.calendarApi.createCalendarEntry(this.toSaveData());
      const entry = this.unwrapResponse(response);
      if (!entry) {
        return;
      }

      this.notificationService.notify('calendar', 'Событие добавлено', entry.title);
      this.successMessage = 'Событие добавлено.';
      this.modal = null;
      this.selectedEntry = null;
      await this.loadEntries();
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public async deleteSelectedEntry(): Promise<void> {
    if (!this.selectedEntry || !window.confirm('Удалить событие "' + this.selectedEntry.title + '"?')) {
      return;
    }

    try {
      const response = await this.calendarApi.deleteCalendarEntry(this.selectedEntry.id);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }

      this.notificationService.notify('calendar', 'Событие удалено', this.selectedEntry.title);
      this.successMessage = 'Событие удалено.';
      this.closeModal();
      await this.loadEntries();
    } catch (error) {
      this.handleUnknownError(error);
    }
  }

  public getPriorityName(priority: WorkspacePriority): string {
    return this.priorityOptions.find((item) => item.value === priority)?.label ?? 'Обычная';
  }

  public formatEntryTime(entry: CalendarEntry): string {
    const formatter = new Intl.DateTimeFormat('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return formatter.format(new Date(entry.startsAt)) + ' - ' + formatter.format(new Date(entry.endsAt));
  }

  public formatEntryDate(value: string): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(value));
  }

  public formatSelectedDate(): string {
    const value = new Intl.DateTimeFormat('ru-RU', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
    }).format(this.selectedDate);
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  public formatCompactDate(value: string): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'short',
    }).format(new Date(value));
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  private async loadEntries(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;
    try {
      const response = await this.calendarApi.getCalendarEntries(this.getMonthParam());
      const entries = this.unwrapResponse(response);
      if (entries) {
        this.entries = entries;
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private createForm(date = new Date()): CalendarEntryForm {
    return {
      title: '',
      date: this.toDateKey(date),
      startsAt: '09:00',
      endsAt: '10:00',
      priority: 'medium',
      description: '',
    };
  }

  private toSaveData(): SaveCalendarEntryData {
    return {
      title: this.formData.title.trim(),
      startsAt: this.toIsoDateTime(this.formData.date, this.formData.startsAt),
      endsAt: this.toIsoDateTime(this.formData.date, this.formData.endsAt),
      priority: this.formData.priority,
      description: this.formData.description.trim() || null,
    };
  }

  private toIsoDateTime(date: string, time: string): string {
    return new Date(date + 'T' + time + ':00').toISOString();
  }

  private getMonthParam(): string {
    return String(this.displayedMonth.getFullYear()) + '-' + String(this.displayedMonth.getMonth() + 1).padStart(2, '0');
  }

  private startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private dateFromKey(value: string): Date {
    const [
      year,
      month,
      day,
    ] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private toDateKey(value: Date): string {
    return String(value.getFullYear()) + '-' + String(value.getMonth() + 1).padStart(2, '0') + '-' + String(value.getDate()).padStart(2, '0');
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }
}
